import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { stylesPdf } from 'lib/workflowPdf';
import { Page, Text, View, Document, PDFDownloadLink, Image, PDFViewer } from '@react-pdf/renderer';
import Dialog from 'components/Dialog';
import useWorkflowPDF from 'pages/WorkflowResult/components/WorkflowPDF/useWorkflowPDF.hook';
import DialogButton from 'components/DialogButton';
import styles from './PrescriptionResultsPDFStyles.module.scss';
import {
  PatientMetaLabels,
  parseText,
} from 'pages/WorkflowResult/components/PatientResults/lib/calculatePatientResults';
import { FollowUpResultPDFFragment } from 'pages/WorkflowResult/components/FollowUpResultPDFFragment';
import PrescriptionResultPDFFragment from 'pages/WorkflowResult/components/PrescriptionResultPDFFragment';
import useDocumentsApi from 'pages/Patient/components/Documents/useDocumentsApi.hook';
import { MIME_TYPE_PDF } from 'lib/document';
import { NewDocument } from 'types';
import formatDate, { DateTimeFormat } from 'lib/dateFormatter';

interface PrescriptionResultsPDFProps {
  isOpen: boolean;
  onClose: () => void;
  caseId?: number;
  formId?: number;
  signatureUrl?: string;
  patientId: number;
  medical: boolean;
}

export const PrescriptionResultsPDF = ({
  isOpen,
  onClose,
  formId,
  caseId,
  patientId,
  signatureUrl,
  medical,
}: PrescriptionResultsPDFProps) => {
  const [pdfURL, setPDFURL] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [urlLoading, setURLLoading] = useState(false);

  // Ensures the user only saves once
  const [savingInProgress, setSavingInProgress] = useState(false);
  const { createDocument } = useDocumentsApi(patientId);

  const PrescriptionPDF = (
    <PrescriptionResultPDFFragment
      formId={formId}
      caseId={caseId}
      medical={medical}
      signatureUrl={signatureUrl}
    />
  );

  const savePrescriptionToDocument = async (): Promise<void> => {
    const fileResult = await fetch(pdfURL);
    const blob = await fileResult.blob();

    const fileName = `${!medical ? 'Non-Medical' : 'Medical'} Prescription Form - ${formatDate(
      DateTimeFormat.DateAndTimeWithoutMeridies12h,
      moment()
    )}.pdf`;
    const fileObject = new File([blob], fileName, { type: MIME_TYPE_PDF });

    const data: NewDocument = {
      name: fileName,
      typeId: 9,
      document: fileObject,
    };

    if (data.document) await createDocument(data);
  };

  async function onSave() {
    setSavingInProgress(true);
    await savePrescriptionToDocument();
    setSavingInProgress(false);
    onClose();
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (!urlLoading && !!generatedUrl && !pdfURL) {
        setPDFURL(generatedUrl);
        setURLLoading(false);
      }
    }, 3000);

    return () => clearTimeout(delayDebounceFn);
  }, [generatedUrl]);

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={onClose}
        title={`Completed ${!medical ? 'Non-Medical' : 'Medical'} Prescription Form`}
      >
        <div className={styles.container}>
          <p className={styles.text}>
            The Prescription Document is being generated and will be available under the Patient
            Documents
          </p>
          <div className={styles.buttons}>
            <DialogButton disabled={!pdfURL || savingInProgress} onClick={onSave}>
              {!pdfURL || savingInProgress ? 'Loading...' : 'Ok'}
            </DialogButton>
          </div>
        </div>
      </Dialog>
      <PDFDownloadLink document={PrescriptionPDF}>
        {({ blob, url, loading, error }) => {
          setURLLoading(loading);
          if (url) {
            setGeneratedUrl(url);
          }
          return <div></div>;
        }}
      </PDFDownloadLink>
    </div>
  );
};
